<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { supabase } from '../db/supabaseClient.js';

const router = useRouter();

const email = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const loading = ref(false);
const error = ref('');
const step = ref(1); // 1 = ingresar correo, 2 = cambiar contraseña

// Password visibility toggles
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

// Modal state
const showModal = ref(false);
const modalType = ref('error');
const modalTitle = ref('');
const modalMessage = ref('');

function showModalDialog(type, title, message) {
    modalType.value = type;
    modalTitle.value = title;
    modalMessage.value = message;
    showModal.value = true;
}

function closeModal() {
    showModal.value = false;
}

async function hashPassword(plain) {
    if (!plain) return '';
    try {
        const enc = new TextEncoder();
        const data = enc.encode(plain);
        const digest = await crypto.subtle.digest('SHA-256', data);
        const arr = Array.from(new Uint8Array(digest));
        return arr.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
        // Fallback simple hash
        let h = 0;
        for (let i = 0; i < plain.length; i++)
            h = (h << 5) - h + plain.charCodeAt(i);
        return String(h >>> 0);
    }
}

async function verifyEmail() {
    error.value = '';

    if (!email.value.trim()) {
        error.value = 'Por favor ingresa tu correo electrónico';
        return;
    }

    loading.value = true;

    try {
        // Verificar si el correo existe en la base de datos
        const { data, error: dbError } = await supabase
            .from('users')
            .select('id_users')
            .eq('email', email.value.trim())
            .limit(1);

        loading.value = false;

        if (dbError) {
            console.error('Error de Supabase:', dbError);
            error.value = 'Error al verificar el correo';
            showModalDialog('error', 'Error', error.value);
            return;
        }

        if (data && data.length > 0) {
            // El correo existe, mostrar campos de contraseña
            step.value = 2;
        } else {
            error.value = 'El correo electrónico no está registrado';
            showModalDialog('error', 'Error', error.value);
        }
    } catch (err) {
        console.error('Error:', err);
        loading.value = false;
        error.value = 'Error al verificar el correo';
        showModalDialog('error', 'Error', error.value);
    }
}

async function changePassword() {
    error.value = '';

    if (!newPassword.value.trim()) {
        error.value = 'Por favor ingresa la nueva contraseña';
        return;
    }

    if (newPassword.value.length < 6) {
        error.value = 'La contraseña debe tener al menos 6 caracteres';
        return;
    }

    if (newPassword.value !== confirmPassword.value) {
        error.value = 'Las contraseñas no coinciden';
        return;
    }

    loading.value = true;

    try {
        // Hash de la contraseña
        const hashedPassword = await hashPassword(newPassword.value);

        console.log('Actualizando contraseña para:', email.value.trim());

        // Actualizar la contraseña del usuario (la columna se llama password_hash)
        const { data, error: dbError } = await supabase
            .from('users')
            .update({ password_hash: hashedPassword })
            .eq('email', email.value.trim())
            .select();

        loading.value = false;

        if (dbError) {
            console.error('Error de Supabase:', dbError);
            error.value = `No se pudo cambiar la contraseña: ${dbError.message}`;
            showModalDialog('error', 'Error', error.value);
            return;
        }

        console.log('Resultado de actualización:', data);

        showModalDialog('success', 'Contraseña cambiada', 'Tu contraseña ha sido actualizada exitosamente.');

        // Redirigir al login después de 2 segundos
        setTimeout(() => {
            router.push('/login');
        }, 2000);
    } catch (err) {
        console.error('Error:', err);
        loading.value = false;
        error.value = 'Error al cambiar la contraseña';
        showModalDialog('error', 'Error', error.value);
    }
}

function goBack() {
    step.value = 1;
    newPassword.value = '';
    confirmPassword.value = '';
    error.value = '';
    showNewPassword.value = false;
    showConfirmPassword.value = false;
}
</script>

<template>
    <main class="reset-container">
        <!-- Custom Modal -->
        <Transition name="modal">
            <div v-if="showModal" class="modal-overlay" @click="closeModal">
                <div class="modal-container" @click.stop>
                    <div class="modal-icon" :class="modalType">
                        <span class="material-symbols-rounded">
                            {{ modalType === 'error' ? 'cancel' : 'check_circle' }}
                        </span>
                    </div>
                    <h3 class="modal-title">{{ modalTitle }}</h3>
                    <p class="modal-message">{{ modalMessage }}</p>
                    <button class="modal-btn" @click="closeModal">OK</button>
                </div>
            </div>
        </Transition>

        <div class="reset-wrapper">
            <!-- Left Side - Form -->
            <div class="form-side">
                <div class="form-content">
                    <div class="header">
                        <h1 class="welcome-text">Recuperar contraseña</h1>
                        <h2 class="logo-text" style="font-size: 3.5rem; margin-bottom: 0.5rem;">Sushito</h2>
                        <p class="subtitle" v-if="step === 1">
                            Ingresa tu correo electrónico para continuar
                        </p>
                        <p class="subtitle" v-else>
                            Ingresa tu nueva contraseña
                        </p>
                    </div>

                    <!-- Paso 1: Ingresar correo -->
                    <Transition name="slide" mode="out-in">
                        <form v-if="step === 1" key="step1" @submit.prevent="verifyEmail" class="reset-form">
                            <Transition name="slide-fade">
                                <div v-if="error" class="error-message">
                                    <span class="material-symbols-rounded">error</span>
                                    <span>{{ error }}</span>
                                </div>
                            </Transition>

                            <!-- Email Input -->
                            <div class="input-group">
                                <label class="input-label">
                                    <span class="material-symbols-rounded">email</span>
                                    Correo electrónico
                                </label>
                                <div class="input-wrapper">
                                    <span class="input-icon">
                                        <span class="material-symbols-rounded">email</span>
                                    </span>
                                    <input v-model="email" type="email" placeholder="tu@email.com" class="input-field"
                                        autofocus autocomplete="email" />
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <button type="submit" class="submit-btn" :disabled="loading">
                                <span v-if="!loading" class="btn-content">
                                    <span>CONTINUAR</span>
                                    <span class="material-symbols-rounded">arrow_forward</span>
                                </span>
                                <span v-else class="btn-content">
                                    <span class="loading-spinner"></span>
                                    <span>Verificando...</span>
                                </span>
                            </button>

                            <div class="form-links">
                                <router-link to="/login" class="link-item">
                                    <span class="material-symbols-rounded">login</span>
                                    <span>¿Recordaste tu contraseña? Iniciar sesión</span>
                                </router-link>
                            </div>
                        </form>

                        <!-- Paso 2: Cambiar contraseña -->
                        <form v-else key="step2" @submit.prevent="changePassword" class="reset-form">
                            <Transition name="slide-fade">
                                <div v-if="error" class="error-message">
                                    <span class="material-symbols-rounded">error</span>
                                    <span>{{ error }}</span>
                                </div>
                            </Transition>

                            <!-- Email Display (read-only) -->
                            <div class="input-group">
                                <label class="input-label">
                                    <span class="material-symbols-rounded">email</span>
                                    Correo electrónico
                                </label>
                                <div class="input-wrapper input-disabled">
                                    <span class="input-icon">
                                        <span class="material-symbols-rounded">email</span>
                                    </span>
                                    <input :value="email" type="email" class="input-field" readonly disabled />
                                </div>
                            </div>

                            <!-- New Password Input -->
                            <div class="input-group">
                                <label class="input-label">
                                    <span class="material-symbols-rounded">lock</span>
                                    Nueva contraseña
                                </label>
                                <div class="input-wrapper password-wrapper">
                                    <span class="input-icon">
                                        <span class="material-symbols-rounded">lock</span>
                                    </span>
                                    <input v-model="newPassword" :type="showNewPassword ? 'text' : 'password'"
                                        placeholder="Mínimo 6 caracteres" class="input-field" autofocus
                                        autocomplete="new-password" />
                                    <button type="button" class="toggle-password"
                                        @click="showNewPassword = !showNewPassword" tabindex="-1">
                                        <span class="material-symbols-rounded">
                                            {{ showNewPassword ? 'visibility_off' : 'visibility' }}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <!-- Confirm Password Input -->
                            <div class="input-group">
                                <label class="input-label">
                                    <span class="material-symbols-rounded">lock_reset</span>
                                    Confirmar contraseña
                                </label>
                                <div class="input-wrapper password-wrapper">
                                    <span class="input-icon">
                                        <span class="material-symbols-rounded">lock_reset</span>
                                    </span>
                                    <input v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                                        placeholder="Confirma tu contraseña" class="input-field"
                                        autocomplete="new-password" />
                                    <button type="button" class="toggle-password"
                                        @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
                                        <span class="material-symbols-rounded">
                                            {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <button type="submit" class="submit-btn" :disabled="loading">
                                <span v-if="!loading" class="btn-content">
                                    <span>CAMBIAR CONTRASEÑA</span>
                                    <span class="material-symbols-rounded">check_circle</span>
                                </span>
                                <span v-else class="btn-content">
                                    <span class="loading-spinner"></span>
                                    <span>Cambiando...</span>
                                </span>
                            </button>

                            <div class="form-links">
                                <a @click.prevent="goBack" class="link-item">
                                    <span class="material-symbols-rounded">arrow_back</span>
                                    <span>Volver atrás</span>
                                </a>
                            </div>
                        </form>
                    </Transition>
                </div>
            </div>

            <!-- Right Side - Image -->
            <div class="illustration-side">
                <div class="image-container">
                    <img src="/img/sushi_2.png" alt="Sushito" class="forgot-image" />
                    <div class="image-overlay"></div>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Cinzel:wght@600;700;800&display=swap');

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.reset-container {
    display: flex;
    min-height: 100vh;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
}

.reset-wrapper {
    max-width: 1000px;
    width: 100%;
    display: flex;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    overflow: hidden;
    background: rgba(20, 24, 30, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(198, 146, 61, 0.2);
    animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ===== LEFT SIDE - FORM ===== */
.form-side {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 2rem;
    background: transparent;
    overflow: hidden;
}

.form-content {
    width: 100%;
    max-width: 380px;
    animation: fadeIn 0.8s ease-out 0.2s both;
}

.form-content::-webkit-scrollbar {
    width: 4px;
}

.form-content::-webkit-scrollbar-track {
    background: transparent;
}

.form-content::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
}

.form-content::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.header {
    margin-bottom: 1.2rem;
    text-align: center;
}

.welcome-text {
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.2rem;
    letter-spacing: 0.5px;
}

.brand-name {
    font-size: 1.8rem;
    font-weight: 900;
    font-family: 'Playfair Display', 'Cinzel', 'Times New Roman', serif;
    text-transform: uppercase;
    background: linear-gradient(135deg,
            #FFD700 0%,
            #FFE55C 10%,
            #FFD700 20%,
            #F4D03F 35%,
            #FFD700 45%,
            #C6923D 60%,
            #9D6B27 75%,
            #C6923D 85%,
            #FFD700 95%,
            #FFE55C 100%);
    background-size: 300% 300%;
    animation: shimmer 2.5s ease-in-out infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.2em;
    margin: 0 0 0.3rem 0;
    text-shadow:
        3px 4px 0px rgba(0, 0, 0, 1),
        5px 7px 12px rgba(0, 0, 0, 0.9),
        7px 10px 20px rgba(0, 0, 0, 0.8),
        0px 2px 4px rgba(255, 215, 0, 0.6),
        0px -2px 2px rgba(114, 68, 16, 0.8),
        inset 0px 1px 0px rgba(255, 255, 255, 0.3);
    filter:
        drop-shadow(3px 5px 10px rgba(0, 0, 0, 0.9)) drop-shadow(1px 2px 4px rgba(255, 215, 0, 0.4));
    position: relative;
    transform: perspective(100px) rotateX(2deg);
}

@keyframes shimmer {

    0%,
    100% {
        background-position: 0% 50%;
    }

    50% {
        background-position: 100% 50%;
    }
}

.subtitle {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    font-weight: 400;
}

.reset-form {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.error-message {
    padding: 0.85rem 1rem;
    background: linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.1) 100%);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 107, 107, 0.3);
    border-left: 4px solid #ff6b6b;
    color: #ff6b6b;
    font-size: 0.85rem;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.1);
}

.error-message .material-symbols-rounded {
    font-size: 1.2rem;
}

/* Input Groups */
.input-group {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.input-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.input-label .material-symbols-rounded {
    font-size: 1.1rem;
    color: #C6923D;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 2px solid rgba(198, 146, 61, 0.2);
    border-radius: 14px;
    transition: all 0.3s ease;
    overflow: hidden;
}

.input-wrapper:focus-within {
    border-color: #C6923D;
    box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.15),
        0 4px 12px rgba(198, 146, 61, 0.2);
    transform: translateY(-2px);
}

.input-disabled {
    background: rgba(148, 163, 184, 0.1);
    border-color: rgba(148, 163, 184, 0.2);
    opacity: 0.7;
}

.input-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1rem;
    color: #C6923D;
    pointer-events: none;
}

.input-icon .material-symbols-rounded {
    font-size: 1.3rem;
}

.input-field {
    flex: 1;
    padding: 0.95rem 1rem 0.95rem 0;
    border: none;
    background: transparent;
    font-size: 0.95rem;
    font-family: 'Poppins', sans-serif;
    color: #ffffff;
    transition: all 0.3s;
    outline: none;
}

.input-field::placeholder {
    color: rgba(255, 255, 255, 0.5);
    font-weight: 400;
}

.input-field:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    color: #64748b;
}

.password-wrapper {
    position: relative;
}

.password-wrapper .input-icon {
    padding-right: 0.5rem;
}

.toggle-password {
    position: absolute;
    right: 0.75rem;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
    border-radius: 8px;
}

.toggle-password:hover {
    color: #C6923D;
    background: rgba(198, 146, 61, 0.15);
}

.toggle-password:active {
    transform: scale(0.95);
}

.toggle-password .material-symbols-rounded {
    font-size: 1.3rem;
}

/* Submit Button */
.submit-btn {
    width: 100%;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
    color: #000000;
    border: none;
    border-radius: 14px;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 0.5rem;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(198, 146, 61, 0.4);
}

.submit-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #DFC05E 0%, #F7ED7D 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.submit-btn:hover:not(:disabled)::before {
    opacity: 1;
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(198, 146, 61, 0.5);
}

.submit-btn:active:not(:disabled) {
    transform: translateY(-1px);
}

.submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
}

.btn-content {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
}

.btn-content .material-symbols-rounded {
    font-size: 1.2rem;
    transition: transform 0.3s ease;
}

.submit-btn:hover:not(:disabled) .btn-content .material-symbols-rounded {
    transform: translateX(4px);
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid rgba(0, 0, 0, 0.3);
    border-top-color: #000000;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

/* Links */
.form-links {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.5rem;
}

.link-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 0.75rem 1rem;
    background: rgba(198, 146, 61, 0.08);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(198, 146, 61, 0.2);
    border-radius: 12px;
    color: #C6923D;
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 600;
    transition: all 0.3s ease;
    cursor: pointer;
}

.link-item:hover {
    background: rgba(198, 146, 61, 0.15);
    border-color: rgba(198, 146, 61, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.link-item .material-symbols-rounded {
    font-size: 1.1rem;
}

/* ===== RIGHT SIDE - IMAGE ===== */
.illustration-side {
    flex: 1;
    position: relative;
    overflow: hidden;
    background: #000;
}

.image-container {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 500px;
}

.forgot-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transition: transform 0.6s ease;
}

.reset-wrapper:hover .forgot-image {
    transform: scale(1.05);
}

.image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(198, 146, 61, 0.2) 0%, rgba(223, 192, 94, 0.25) 100%);
    pointer-events: none;
}

/* Transitions */
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.4s ease;
}

.slide-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

/* Responsive */
@media (max-width: 968px) {
    .reset-container {
        min-height: 100vh;
        height: auto;
        padding: 0;
        align-items: flex-start;
    }

    .reset-wrapper {
        flex-direction: column;
        max-width: 100%;
        width: 100%;
        min-height: 100vh;
        border-radius: 0;
        overflow-y: auto;
    }

    .illustration-side {
        min-height: 200px;
        max-height: 200px;
        order: -1;
        flex-shrink: 0;
    }

    .image-container {
        min-height: 200px;
        max-height: 200px;
    }

    .form-side {
        padding: 2rem 1.5rem;
        padding-bottom: 3rem;
        flex: 1;
        overflow: visible;
    }

    .form-content {
        max-width: 100%;
        overflow: visible;
    }
}

@media (max-width: 480px) {
    .reset-container {
        padding: 0;
    }

    .reset-wrapper {
        border-radius: 0;
    }

    .illustration-side {
        min-height: 180px;
        max-height: 180px;
    }

    .image-container {
        min-height: 180px;
        max-height: 180px;
    }

    .form-side {
        padding: 1.5rem 1.25rem;
        padding-bottom: 2.5rem;
    }

    .welcome-text {
        font-size: 0.9rem;
    }

    .brand-name {
        font-size: 1.5rem;
    }

    .input-wrapper {
        border-radius: 12px;
    }

    .submit-btn {
        padding: 0.9rem 1.25rem;
        font-size: 0.9rem;
    }

    .form-links {
        margin-top: 1rem;
        padding-bottom: 1rem;
    }
}

/* ===== CUSTOM MODAL ===== */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5000;
    padding: 1rem;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.modal-container {
    background: rgba(20, 24, 30, 0.98);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(198, 146, 61, 0.3);
    border-radius: 24px;
    padding: 2.5rem;
    max-width: 420px;
    width: 100%;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.25rem;
    animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-30px) scale(0.9);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.modal-icon {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: iconPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

@keyframes iconPop {
    0% {
        transform: scale(0) rotate(-180deg);
        opacity: 0;
    }

    50% {
        transform: scale(1.15) rotate(10deg);
    }

    100% {
        transform: scale(1) rotate(0deg);
        opacity: 1;
    }
}

.modal-icon.error {
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
}

.modal-icon.success {
    background: linear-gradient(135deg, #51cf66 0%, #00C2A8 100%);
}

.modal-icon .material-symbols-rounded {
    font-size: 3.5rem;
    color: white;
}

.modal-title {
    font-size: 1.6rem;
    font-weight: 800;
    color: #ffffff;
    margin: 0;
    text-align: center;
    letter-spacing: -0.5px;
}

.modal-message {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    line-height: 1.6;
    margin: 0;
    white-space: pre-line;
}

.modal-btn {
    width: 100%;
    padding: 0.9rem 1.5rem;
    background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
    color: #000000;
    border: none;
    border-radius: 14px;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 0.5rem;
    box-shadow: 0 4px 15px rgba(198, 146, 61, 0.4);
}

.modal-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(198, 146, 61, 0.5);
}

.modal-btn:active {
    transform: translateY(-1px);
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
    transform: scale(0.9) translateY(-30px);
}
</style>
